import { live_game } from '../types/Livegame';
import { convertBettingOdds, getGamesBetOn} from '../utils/oddsUtils';
import { ParlayPayout } from './ParlayPayout';
import { unselectBet } from './LiveOdds';
import AuthModal from './AuthModal';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import { placeBet } from '../api/walletApi';

interface BetCreatorProps {
    games: live_game[];
    selectedBets: any;
    betAmount: number;
    setBetAmount: any;
    setSelectedBets: any;
}

const removeBet = (selectedBets: any, setSelectedBets: any, gameId: string) => {
    const newSet = new Set(selectedBets);
    newSet.delete(gameId)
    setSelectedBets(newSet);
    unselectBet(gameId);
}

const BetCreator = ({ games, selectedBets, setSelectedBets, betAmount, setBetAmount}: BetCreatorProps) => {
    let gamesBetOn = getGamesBetOn(games, selectedBets);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [newestBetId, setNewestBetId] = useState<string | null>(null);
    const [placingBet, setPlacingBet] = useState(false);
    const prevBetCountRef = useRef(0);
    
    // Get authentication and wallet state
    const { currentUser } = useAuth();
    const { wallet, refreshWallet } = useWallet();
    const isLoggedIn = !!currentUser;
    
    // Auto-scroll and animate ONLY when bets are added (not removed)
    useEffect(() => {
        const currentBetCount = gamesBetOn.length;
        const prevBetCount = prevBetCountRef.current;
        
        // Only animate if we're ADDING a bet (count increased)
        if (currentBetCount > prevBetCount && currentBetCount > 0) {
            // Track the newest bet
            const latestBet = gamesBetOn[gamesBetOn.length - 1];
            setNewestBetId(latestBet.game_id);
            
            // Find the scrollable parent container
            const betTab = document.getElementById('bet-tab');
            const scrollableContainer = betTab?.querySelector('.overflow-y-auto');
            
            if (scrollableContainer) {
                // Smooth scroll to bottom
                setTimeout(() => {
                    scrollableContainer.scrollTo({
                        top: scrollableContainer.scrollHeight,
                        behavior: 'smooth'
                    });
                }, 50);
            }
            
            // Remove animation class after animation completes
            setTimeout(() => {
                setNewestBetId(null);
            }, 600);
        }
        
        // Update previous count for next comparison
        prevBetCountRef.current = currentBetCount;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBets, gamesBetOn.length]);

    const handlePlaceBet = async () => {
        // Clear previous messages
        setErrorMessage('');
        setSuccessMessage('');
        const element = document.getElementById('confirm-bet-id');
        
        // Validate bet amount (check for 0, empty, or falsy values)
        if (!betAmount || betAmount <= 0) {
            setErrorMessage('To confirm your wager, please enter a bet amount greater than $0.00.');
            if (element) {
                element.classList.remove('hidden');
            }
            return;
        }
        
        // Validate selections
        if (gamesBetOn.length === 0) {
            setErrorMessage('Bet slip is empty, please select at least one bet.');
            if (element) {
                element.classList.remove('hidden');
            }
            return;
        }
        
        // Check if user is logged in
        if (!isLoggedIn) {
            setShowAuthModal(true);
            return;
        }
        
        // Check if wallet exists
        if (!wallet) {
            setErrorMessage('Wallet not found. Please select a challenge mode first.');
            if (element) {
                element.classList.remove('hidden');
            }
            return;
        }
        
        // Check sufficient balance
        if (wallet.balance < betAmount) {
            setErrorMessage(`Insufficient balance. You have $${wallet.balance.toFixed(2)} available.`);
            if (element) {
                element.classList.remove('hidden');
            }
            return;
        }
        
        // Place the bet
        setPlacingBet(true);
        try {
            const betId = `bet_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            await placeBet(currentUser.uid, betAmount, betId);
            
            // Refresh wallet to get updated balance
            await refreshWallet();
            
            // Show success message
            setSuccessMessage(`Bet placed successfully! $${betAmount.toFixed(2)} wagered.`);
            
            // Clear bet slip after successful placement
            setTimeout(() => {
                selectedBets.forEach((betId: string) => {
                    unselectBet(betId);
                });
                setSelectedBets(new Set());
                setBetAmount(0);
                setSuccessMessage('');
            }, 2000);
            
        } catch (err: any) {
            console.error('Error placing bet:', err);
            setErrorMessage(err.message || 'Failed to place bet. Please try again.');
            if (element) {
                element.classList.remove('hidden');
            }
        } finally {
            setPlacingBet(false);
        }
    };

    return (
        <>
            <AuthModal 
                isVisible={showAuthModal}
                onClose={() => setShowAuthModal(false)}
                initialMode='login'
            />
            
            <div className='bg-gradient-to-r from-gray-900/50 to-gray-900/30 border border-cyan-500/10 hover:border-cyan-500/30 transition-all p-0 overflow-hidden backdrop-blur-sm rounded-xl'>
                <div className='flex items-center flex-col mt-5'>
                    <h1 className='text-cyan-500 text-xl'>Parlay Builder</h1>
                    <h2 className='text-gray-500 text-xs'>{gamesBetOn.length} Selections</h2>
                <div className=''>
                    {gamesBetOn.map(game => 
                    <div 
                        key={game.game_id} 
                        className={`m-5 bg-gradient-to-r from-gray-900/50 to-gray-900/30 border border-cyan-500/10 p-5 text-xs rounded-xl transition-all ${
                            newestBetId === game.game_id ? 'animate-betPop' : ''
                        }`}
                    > 
                        <div className='flex justify-between'>
                            <div>
                                <div className='text-xs text-gray-500 my-1'>{game.home_team} vs {game.away_team}</div>
                                {/* SHOULD ONLY USE IF h2h */}
                                <div className='text-sm'>{game.team_bet_on} ML</div>
                                <p className='text-cyan-500 my-1'>Odds: <strong>{convertBettingOdds(game.team_odds)}</strong></p>
                            </div>
                            {/* will have to get the actual betId */}
                            <button onClick={() => removeBet(selectedBets, setSelectedBets, game.team_bet_on_id)} className='mt-2 text-md text-xl font-semibold text-gray-500 hover:text-gray-300'>
                                &times;
                            </button>
                        </div>
                    </div>)}
                </div>
                {/* HORIZONTAL LINE HERE */}
                <p className='text-xs text-left'>Bet Amount</p>
                <div className="flex rounded-lg 
                        border border-cyan-500/10
                        transition duration-150 ease-in-out m-5 bg-gradient-to-r from-gray-900/50 to-gray-900/30 mt-2">
                
                <span className="inline-flex items-center px-4 rounded-l-lg 
                            text-gray-500 text-base font-medium 
                            bg-gray-900/50">
                    $
                </span>
                <input 
                    type="number" 
                    id="short-input" 
                    placeholder="0.00"
                    className="block w-full py-2 pl-2 pr-4 text-white 
                           bg-gray-900/50 placeholder:text-white
                           border-0 focus:ring-0 focus:outline-none rounded-r-lg"
                        value={betAmount}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (value === '') {
                                setBetAmount('');
                                return;
                            }
                            
                            const decimalIndex = value.indexOf('.');
                            if (decimalIndex !== -1) {
                                const decimalPart = value.substring(decimalIndex + 1);
                                if (decimalPart.length > 2) {
                                    const truncatedValue = value.substring(0, decimalIndex + 3);
                                    const numValue = parseFloat(truncatedValue);
                                    if (!isNaN(numValue)) {
                                        setBetAmount(Math.max(numValue, 0));
                                    }
                                    return;
                                }
                            }
                            
                            const validPattern = /^\d*\.?\d{0,2}$/;
                            if (!validPattern.test(value)) {
                                return;
                            }
                            
                            const numValue = parseFloat(value);
                            if (isNaN(numValue)) {
                                return;
                            }
                            const positiveValue = Math.max(numValue, 0);
                            setBetAmount(positiveValue);
                        }}
                        onBlur={(e) => {
                            const value = parseFloat(e.target.value);
                            if (!isNaN(value)) {
                                const roundedValue = Math.round(Math.max(value, 0) * 100) / 100;
                                setBetAmount(roundedValue);
                            }
                        }}
                        min="0"
                        step="0.01"
                        required
                />
            </div>
            <ParlayPayout
                games={games}
                selectedBets={selectedBets}
                betAmount={betAmount}
            />
            
            {/* Error message display */}
            {errorMessage && (
                <div id='confirm-bet-id' className='text-center text-xs mt-4 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 animate-fadeIn'>
                    <p className='text-red-400'>{errorMessage}</p>
                </div>
            )}
            
            {/* Success message display */}
            {successMessage && (
                <div className='text-center text-xs mt-4 px-4 py-2 rounded-lg bg-green-500/10 border border-green-500/30 animate-fadeIn'>
                    <p className='text-green-400'>{successMessage}</p>
                </div>
            )}
            
            <button 
                onClick={handlePlaceBet}
                disabled={placingBet}
                className={`
                    mt-4 p-5 w-full mb-0 transition-all duration-200 font-semibold text-white
                    ${placingBet 
                        ? 'bg-gray-600 cursor-not-allowed' 
                        : 'bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 hover:cursor-pointer transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg hover:shadow-cyan-500/50'
                    }
                `}
            >
                {placingBet ? (
                    <span className='flex items-center justify-center gap-2'>
                        <div className='animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white'></div>
                        Placing Bet...
                    </span>
                ) : (
                    'Confirm Bet'
                )}
            </button>
            </div>
        </div>
        </>
    )
};

export default BetCreator;